import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { graphBuilderApi } from '../../../services/api';
import ExecutionInsightDetail from '../../UserHome/ExecutionInsightDetail';
import EvidenceTrailPopup from '../../UserHome/EvidenceTrailPopup';
import CaseEvidenceFrameView from '../execution/CaseEvidenceFrameView';
import ViolationsReportView from '../execution/ViolationsReportView';
import AggregateDecisionCard from '../execution/AggregateDecisionCard';
import '../../UserHome/ExecutionInsights.css';

/**
 * Full-width below-map insights panel for A/B process nodes.
 * SNN nodes use ExecutionInsightDetail (same as test graph page).
 * LLM nodes show prompt, response, tokens, and model info.
 */
export default function ABNodeInsightsPanel({ nodeId, nodeData, executionId, onClose }) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!executionId || !nodeId) return;
    let cancelled = false;
    setLoading(true);
    graphBuilderApi.getNodeResult(executionId, nodeId)
      .then((r) => { if (!cancelled) setResult(r); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [executionId, nodeId]);

  const isSNN = nodeData?.executionType === 'snn_pipeline';
  const isLLM = nodeData?.executionType === 'llm';
  const isCaseExtractor = nodeData?.executionType === 'case_extractor';
  const isChainReasoner = nodeData?.executionType === 'chain_reasoner';
  const isCrossLink = nodeData?.executionType === 'cross_link';
  const crossLink = result?.metadata?.cross_link || null;
  const status = nodeData?.status || 'pending';

  const caseEvidenceFrame = useMemo(() => {
    if (!result) return null;
    return result.case_evidence_frame
      || result.outputs_produced?.case_evidence_frame
      || null;
  }, [result]);

  const violationsReport = useMemo(() => {
    if (!result) return null;
    return result.violations_report
      || result.outputs_produced?.violations_report
      || null;
  }, [result]);

  const snnInsight = useMemo(() => {
    if (!isSNN || !result) return null;
    const trace = result.snn_execution_trace;
    return {
      execution_trace: trace,
      prompt_expansions: result.snn_prompt_expansions,
      score: result.snn_score,
      duration_ms: result.duration_ms,
      total_ticks: trace?.total_ticks,
      total_spikes: trace?.total_spikes,
      natural_language_answer: result.llm_response,
      query: result.inputs_used?.nl_query || '',
      llm_model: trace?.rate_coding_params?.llm_model || '',
      snn_execution_mode: '',
    };
  }, [isSNN, result]);

  const [etOpen, setEtOpen] = useState(false);

  const graphId = useMemo(() => {
    if (!result) return null;
    return result.snn_answer_frame?.graph_id
      || result.metadata?.routing?.graph_id
      || null;
  }, [result]);

  const contextAssembly = useMemo(() => {
    return snnInsight?.execution_trace?.context_assembly || null;
  }, [snnInsight]);

  const openEvidence = useCallback(() => setEtOpen(true), []);

  if (!nodeId) return null;

  return (
    <div className="ab-insights-panel">
      <div className="ab-insights-panel-header">
        <div className="ab-insights-panel-title">
          <span className={`type-badge ${nodeData?.executionType}`}>
            {nodeData?.typeLetter}
          </span>
          <h3>{nodeData?.label || nodeId}</h3>
          <span className={`status-pill ${status}`}>{status}</span>
        </div>
        <button className="ab-insights-panel-close" onClick={onClose}>&times;</button>
      </div>

      <div className="ab-insights-panel-body">
        {loading && (
          <div className="drawer-loading">
            <div className="spinner" />
            <span>Loading execution insights...</span>
          </div>
        )}

        {!loading && !result && status !== 'pending' && (
          <p className="drawer-empty">No result data available.</p>
        )}

        {!loading && status === 'pending' && (
          <p className="drawer-empty">Node has not executed yet.</p>
        )}

        {result && isSNN && snnInsight && (snnInsight.execution_trace || snnInsight.prompt_expansions) && (
          <>
            <ExecutionInsightDetail insight={snnInsight} graphId={graphId} />
            {contextAssembly && (
              <div className="insight-section" style={{ paddingTop: 4 }}>
                <button
                  className="execution-insight-link"
                  onClick={openEvidence}
                  style={{ fontSize: 12 }}
                >
                  <span className="insight-link-icon">&#128196;</span>
                  Evidence Trail
                </button>
              </div>
            )}
          </>
        )}

        {result && isSNN && !snnInsight?.execution_trace && !snnInsight?.prompt_expansions && (
          <div className="insight-section">
            <h4 className="insight-section-title">Answer Frame</h4>
            <pre className="code-block">
              {JSON.stringify(result.snn_answer_frame || result.outputs_produced, null, 2)}
            </pre>
          </div>
        )}

        {result && isLLM && (
          <>
            {result.duration_ms != null && (
              <div className="insight-section">
                <h4 className="insight-section-title">Duration</h4>
                <span className="mono">{(result.duration_ms / 1000).toFixed(2)}s</span>
              </div>
            )}

            {(result.prompt_tokens || result.completion_tokens) ? (
              <div className="insight-section">
                <h4 className="insight-section-title">Token Usage</h4>
                <div className="token-grid">
                  <div><span className="dim">Prompt</span><br />{result.prompt_tokens ?? 0}</div>
                  <div><span className="dim">Completion</span><br />{result.completion_tokens ?? 0}</div>
                  <div><span className="dim">Total</span><br />{result.tokens_used ?? 0}</div>
                </div>
              </div>
            ) : null}

            {result.llm_model && (
              <div className="insight-section">
                <h4 className="insight-section-title">Model</h4>
                <span className="mono">{result.llm_model}</span>
              </div>
            )}

            {result.prompt_rendered && (
              <div className="insight-section">
                <h4 className="insight-section-title">Rendered Prompt</h4>
                <pre className="code-block">{result.prompt_rendered}</pre>
              </div>
            )}

            {result.llm_response && (
              <div className="insight-section">
                <h4 className="insight-section-title">LLM Response</h4>
                <pre className="code-block">{result.llm_response}</pre>
              </div>
            )}
          </>
        )}

        {result && isCaseExtractor && caseEvidenceFrame && (
          <div className="insight-section">
            <h4 className="insight-section-title">Case Evidence Frame</h4>
            <CaseEvidenceFrameView frame={caseEvidenceFrame} />
          </div>
        )}

        {result && isChainReasoner && violationsReport && (
          <>
            <div className="insight-section">
              <AggregateDecisionCard report={violationsReport} title="Aggregate decision" />
            </div>
            <div className="insight-section">
              <h4 className="insight-section-title">Violations Report</h4>
              <ViolationsReportView report={violationsReport} />
            </div>
          </>
        )}

        {result && isCrossLink && crossLink && (
          <div className="insight-section">
            <h4 className="insight-section-title">Cross-Link</h4>
            <pre className="code-block">{JSON.stringify({
              target_type: crossLink.target_type,
              target: crossLink.target_process_name || crossLink.target_process_id
                || crossLink.graph_id || crossLink.router_graph_id,
              child_status: crossLink.child_status,
              child_execution_id: crossLink.child_execution_id,
              child_persisted: crossLink.child_persisted,
              child_duration_ms: crossLink.child_duration_ms,
              completed_nodes: crossLink.completed_nodes,
              failed_nodes: crossLink.failed_nodes,
              depth: crossLink.depth,
            }, null, 2)}</pre>
          </div>
        )}

        {result && !isSNN && !isLLM && !isCaseExtractor && !isChainReasoner && result.outputs_produced && (
          <div className="insight-section">
            <h4 className="insight-section-title">Output</h4>
            <pre className="code-block">{JSON.stringify(result.outputs_produced, null, 2)}</pre>
          </div>
        )}
      </div>

      <EvidenceTrailPopup
        open={etOpen}
        onClose={() => setEtOpen(false)}
        evidenceNodes={contextAssembly?.evidence_nodes}
        verbatimNodes={contextAssembly?.verbatim_nodes}
      />
    </div>
  );
}
